<template>
    <div>
        <!-- Modals -->
        <!-- NOTE - Add new Account -->
        <b-modal
            size="lg"
            ref="add-account-modal"
            id="add-account-modal"
            centered
            title="Add new Account"
        >
            <!-- Preloader -->
            <PagePreloader v-if="modalIsLoading"></PagePreloader>

            <!-- Error management -->
            <CustomAlerts :errors="modalerrors"></CustomAlerts>

            <p>
                To add an account to the Hashtastic Insights Tracker, you will need to link your
                Instagram Account with Hashtastic via Facebook. Click the button below to be
                redirected to Facebook.
            </p>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button
                    type="submit"
                    variant="success"
                    @click="addAccount"
                    :disabled="modalIsLoading"
                >
                    Add Account
                </b-button>
                <!-- Button with custom close trigger value -->
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>
        <!-- End add account modal -->

        <!-- NOTE - Delete account modal -->
        <b-modal
            ref="modal-delete"
            id="modal-delete"
            centered
            size="lg"
            title="Are you sure you want to stop tracking this account?"
        >
            <!-- Error management -->
            <CustomAlerts :errors="modalerrors"></CustomAlerts>

            <!-- Preloader -->
            <PagePreloader v-if="modalisloading"></PagePreloader>

            <!-- Content -->
            <template v-if="accountData.account_data">
                <p class="lead">
                    Are you sure you want to stop tracking
                    <code>@{{ accountData.account_data.username }}</code>
                </p>
            </template>

            <template v-slot:modal-footer="{ ok, cancel, hide }">
                <b-button variant="danger" @click="deleteAccount()" :disabled="modalisloading">
                    Remove Account
                </b-button>
                <b-button variant="outline-secondary" @click="hide()"> Cancel </b-button>
            </template>
        </b-modal>

        <!-- NOTE - Content Main -->
        <div class="row">
            <div class="col-xl-3 d-print-none">
                <!-- Page Preloader -->
                <PagePreloader v-if="!pageIsLoaded"></PagePreloader>

                <div class="card card-custom card-stretch gutter-b">
                    <div class="card-header">
                        <div class="card-title">
                            <h3 class="card-label">Tracked Accounts</h3>
                        </div>
                    </div>
                    <div class="card-body">
                        <template v-if="pageIsLoaded">
                            <!-- Check if we actually have accounts first -->
                            <div v-if="myAccounts.length > 0">
                                <!--begin::Nav-->
                                <div
                                    class="navi navi-bold navi-hover navi-active navi-link-rounded"
                                >
                                    <transition-group name="list">
                                        <div v-for="account in myAccounts" :key="account.userid">
                                            <div class="navi-item">
                                                <a
                                                    @click="clickSearch(account)"
                                                    class="navi-link py-4 campaign-link"
                                                >
                                                    <span class="navi-icon mr-2">
                                                        <span class="svg-icon">
                                                            <b-img
                                                                v-bind:src="account.profilepic"
                                                                rounded
                                                                fluid
                                                            ></b-img>
                                                        </span>
                                                    </span>
                                                    <span
                                                        class="navi-text font-size-lg"
                                                        :class="
                                                            currentAccount == account.username
                                                                ? 'text-success'
                                                                : ''
                                                        "
                                                    >
                                                        {{ account.username }}
                                                        <span
                                                            class="
                                                                navi-text
                                                                font-size-sm
                                                                text-muted
                                                                d-block
                                                            "
                                                        >
                                                            {{ account.followers_pretty }}
                                                            followers
                                                        </span>
                                                    </span>
                                                </a>
                                                <div
                                                    class="
                                                        separator
                                                        separator-dashed
                                                        separator-border-1
                                                    "
                                                ></div>
                                            </div>
                                        </div>
                                    </transition-group>
                                </div>
                                <!--end::Nav-->
                            </div>
                            <div v-else>
                                <p>
                                    It looks like you aren't tracking any accounts. Use the form on
                                    the right to start looking for accounts to track.
                                </p>
                            </div>
                            <b-button
                                variant="success"
                                class="col-12 gutter-t"
                                @click="openAddAccountModal"
                                >Add new Account</b-button
                            >
                        </template>
                    </div>
                </div>
            </div>
            <!-- End col 3 -->

            <div class="col-xl-9">
                <!-- Error management -->
                <CustomAlerts :errors="errors"></CustomAlerts>

                <!-- Page Preloader -->
                <PagePreloader v-if="loadingSearchResults">results</PagePreloader>

                <!-- Check to see if we should display account -->
                <div v-if="displayAccountData">
                    <!-- Display Account Badge -->
                    <AccountBadgeWide :account="accountData.account_data">
                        <template v-slot:actions>
                            <div class="my-lg-0 my-1 d-print-none">
                                <b-button-group>
                                    <!-- <button
                                        v-if="isEnterprise"
                                        type="button"
                                        class="btn btn-light-primary font-weight-bolder text-uppercase"
                                        @click="printReport(accountData.user.userid)"
                                    >
                                        <i class="fas fa-download ml-2"></i> Export
                                    </button> -->
                                    <!-- <template v-if="accountData.user.has_campaign">
                                        <b-button
                                            @click="viewCampaign(accountData.user.userid)"
                                            class="btn btn-sm btn-light-success font-weight-bolder text-uppercase"
                                        >
                                            View Campaign
                                        </b-button>
                                    </template> -->
                                    <button
                                        type="button"
                                        class="
                                            btn btn-light-danger
                                            font-weight-bolder
                                            text-uppercase
                                        "
                                        @click="openDeleteModal"
                                    >
                                        Delete
                                    </button>
                                </b-button-group>
                            </div>
                        </template>
                    </AccountBadgeWide>
                </div>
                <div v-else>
                    <div class="card card-custom gutter-b">
                        <div class="card-header">
                            <div class="card-title">
                                <span class="card-icon">
                                    <i class="flaticon2-chat-1 text-primary"></i>
                                </span>
                                <h3 class="card-label">Instagram Insights (BETA)</h3>
                            </div>
                        </div>
                        <div class="card-body">
                            <p class="lead">
                                Welcome to your Instagram Insights Scanner. Please either select an
                                account, or add a new account to be tracked.
                            </p>
                            <p>
                                This tool enables you to visualise your Instagram insights over
                                time, including user demographics, reach, impressions, and other
                                advanced insights for your account.
                            </p>
                            <p>
                                Please note this tool is still in Beta, and we'll be rolling out
                                many updates in the coming days.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <!-- END Col 9 -->
        </div>

        <!-- Start col 12 insights -->
        <template v-if="displayAccountData">
            <template v-if="accountData.insights">
                <!-- Content -->
                <div class="row">
                    <!--begin::Aside-->
                    <div class="col-xl-3 gutter-b">
                        <!--begin::Profile Card-->
                        <div class="card card-custom">
                            <div class="card-header py-3">
                                <div class="card-title align-items-start flex-column">
                                    <h3 class="card-label font-weight-bolder text-dark">
                                        Your Profile
                                    </h3>
                                    <span class="text-muted font-weight-bold font-size-sm mt-1"
                                        >View your profile &amp; settings</span
                                    >
                                </div>
                            </div>
                            <!--begin::Body-->
                            <div class="card-body pt-4">
                                <!--begin::Nav-->
                                <div
                                    class="
                                        navi navi-bold navi-hover navi-active navi-link-rounded
                                        py-2
                                    "
                                >
                                    <!-- NOTE - Growth -->
                                    <div class="navi-item mb-2">
                                        <a
                                            @click="setPanel('growth')"
                                            class="navi-link py-4"
                                            :class="currentPanel == 'growth' ? 'active' : ''"
                                        >
                                            <span class="navi-icon mr-2">
                                                <span class="svg-icon">
                                                    <inline-svg
                                                        src="/media/svg/icons/Shopping/Chart-line1.svg"
                                                    ></inline-svg>
                                                </span>
                                            </span>
                                            <span class="navi-text font-size-lg">
                                                Account Growth
                                            </span>
                                        </a>
                                    </div>

                                    <!-- NOTE - Demographics -->
                                    <div class="navi-item mb-2">
                                        <a
                                            @click="setPanel('demographics')"
                                            class="navi-link py-4"
                                            :class="currentPanel == 'demographics' ? 'active' : ''"
                                        >
                                            <span class="navi-icon mr-2">
                                                <span class="svg-icon">
                                                    <inline-svg
                                                        src="/media/svg/icons/Communication/Group.svg"
                                                    ></inline-svg>
                                                </span>
                                            </span>
                                            <span class="navi-text font-size-lg">
                                                Audience Demographics
                                            </span>
                                        </a>
                                    </div>

                                    <!-- NOTE - Posts -->
                                    <div class="navi-item mb-2">
                                        <a
                                            @click="setPanel('posts')"
                                            class="navi-link py-4"
                                            :class="currentPanel == 'posts' ? 'active' : ''"
                                        >
                                            <span class="navi-icon mr-2">
                                                <span class="svg-icon">
                                                    <inline-svg
                                                        src="/media/svg/icons/Design/Image.svg"
                                                    ></inline-svg>
                                                </span>
                                            </span>
                                            <span class="navi-text font-size-lg">
                                                Posts [BETA]</span
                                            >
                                        </a>
                                    </div>
                                </div>
                                <!--end::Nav-->
                            </div>
                            <!--end::Body-->
                        </div>
                        <!--end::Profile Card-->
                    </div>
                    <!--end::Aside-->

                    <!-- Start Groups -->
                    <transition-group name="fade-in-up" tag="div" class="col-xl-9">
                        <!-- NOTE - Growth Panel -->
                        <div v-if="currentPanel == 'growth'" key="1">
                            <!-- NOTE - Impressions -->
                            <template v-if="accountData.insights.impressions.length">
                                <!--begin::Card-->
                                <div class="card card-custom gutter-b">
                                    <!--begin::Header-->
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Reach &amp; Impressions
                                            </h3>
                                            <span
                                                class="
                                                    text-muted
                                                    font-weight-bold font-size-sm
                                                    mt-1
                                                "
                                                >Over the last 30 days</span
                                            >
                                        </div>
                                    </div>
                                    <!--end::Header-->
                                    <!-- Body -->
                                    <div class="card-body">
                                        <apexchart
                                            :options="reachImpressionsOptions"
                                            :series="reachImpressionsSeries"
                                            type="area"
                                            height="300px"
                                        ></apexchart>
                                        <apexchart
                                            :options="reachImpressionsByMonthOptions"
                                            :series="reachImpressionsByMonthSeries"
                                            type="area"
                                            height="300px"
                                        ></apexchart>
                                    </div>
                                    <!-- End Body -->
                                </div>
                            </template>
                            <!--end:: Card -->
                            <!-- NOTE - Growth -->
                            <template v-if="accountData.insights.growth.length">
                                <!--begin::Card-->
                                <div class="card card-custom gutter-b">
                                    <!--begin::Header-->
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Follower Growth
                                            </h3>
                                            <span
                                                class="
                                                    text-muted
                                                    font-weight-bold font-size-sm
                                                    mt-1
                                                "
                                                >Since adding to Hashtastic</span
                                            >
                                        </div>
                                    </div>
                                    <!--end::Header-->
                                    <!-- Body -->
                                    <div class="card-body">
                                        <apexchart
                                            :options="growthFollowerOptions"
                                            :series="growthFollowerSeries"
                                            type="area"
                                        ></apexchart>
                                    </div>
                                    <!-- End Body -->
                                </div>
                            </template>
                        </div>
                        <!--end:: Card -->

                        <!-- NOTE - Demographics Card -->
                        <div v-if="currentPanel == 'demographics'" key="2">
                            <!-- NOTE - Empty displayer -->
                            <template
                                v-if="
                                    !accountData.insights.audience_country.length &&
                                    !accountData.insights.audience_city.length &&
                                    !accountData.insights.audience_gender_age.length
                                "
                            >
                                <div class="card card-custom gutter-b">
                                    <div class="card-header">
                                        <div class="card-title">
                                            <span class="card-icon">
                                                <i class="flaticon2-chat-1 text-primary"></i>
                                            </span>
                                            <h3 class="card-label">
                                                No insights available for the moment
                                            </h3>
                                        </div>
                                    </div>
                                    <div class="card-body">
                                        <p class="lead">
                                            There are currently no insights available for this
                                            category. Please try again later.
                                        </p>
                                    </div>
                                </div>
                            </template>
                            <!-- NOTE - Audience Country -->
                            <template v-if="accountData.insights.audience_country.length">
                                <!--begin::Card-->
                                <div class="card card-custom gutter-b">
                                    <!--begin::Header-->
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Audience Country
                                            </h3>
                                            <span
                                                class="
                                                    text-muted
                                                    font-weight-bold font-size-sm
                                                    mt-1
                                                "
                                                >Main countries that make up your followers</span
                                            >
                                        </div>
                                    </div>
                                    <!--end::Header-->
                                    <!-- Body -->
                                    <div class="card-body">
                                        <apexchart
                                            :options="audienceCountryOptions"
                                            :series="audienceCountrySeries"
                                            type="bar"
                                            height="300px"
                                        ></apexchart>
                                    </div>
                                    <!-- End Body -->
                                </div>
                            </template>
                            <!-- END -->
                            <!-- NOTE - Audience City -->
                            <template v-if="accountData.insights.audience_city.length">
                                <!--begin::Card-->
                                <div class="card card-custom gutter-b">
                                    <!--begin::Header-->
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Audience Cities
                                            </h3>
                                            <span
                                                class="
                                                    text-muted
                                                    font-weight-bold font-size-sm
                                                    mt-1
                                                "
                                                >Main cities that make up your followers</span
                                            >
                                        </div>
                                    </div>
                                    <!--end::Header-->
                                    <!-- Body -->
                                    <div class="card-body">
                                        <apexchart
                                            :options="audienceCityOptions"
                                            :series="audienceCitySeries"
                                            type="bar"
                                            height="300px"
                                        ></apexchart>
                                    </div>
                                    <!-- End Body -->
                                </div>
                            </template>
                            <!-- END -->
                            <!-- NOTE - Audience Gender & Age -->
                            <template v-if="accountData.insights.audience_gender_age.length">
                                <!--begin::Card-->
                                <div class="card card-custom gutter-b">
                                    <!--begin::Header-->
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Gender &amp; Age Split
                                            </h3>
                                            <span
                                                class="
                                                    text-muted
                                                    font-weight-bold font-size-sm
                                                    mt-1
                                                "
                                                >Follower demographics</span
                                            >
                                        </div>
                                    </div>
                                    <!--end::Header-->
                                    <!-- Body -->
                                    <div class="card-body">
                                        <apexchart
                                            :options="audienceGenderOptions"
                                            :series="audienceGenderSeries"
                                            type="bar"
                                            height="300px"
                                        ></apexchart>
                                    </div>
                                    <!-- End Body -->
                                </div>
                            </template>
                            <!-- END -->
                            <template v-if="accountData.insights.audience_locale.length">
                                <!--begin::Card-->
                                <div class="card card-custom gutter-b">
                                    <!--begin::Header-->
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Audience Languages
                                            </h3>
                                            <span
                                                class="
                                                    text-muted
                                                    font-weight-bold font-size-sm
                                                    mt-1
                                                "
                                                >Main languages spoken by your audience</span
                                            >
                                        </div>
                                    </div>
                                    <!--end::Header-->
                                    <!-- Body -->
                                    <div class="card-body">
                                        <apexchart
                                            type="donut"
                                            :options="audienceLocaleOptions"
                                            :series="audienceLocaleSeries"
                                            height="350px"
                                        ></apexchart>
                                    </div>
                                    <!-- End Body -->
                                </div>
                            </template>
                            <!-- END -->
                        </div>

                        <!-- NOTE - Growth Panel -->
                        <div v-if="currentPanel == 'posts'" key="3">
                            <!-- NOTE - Impressions -->
                            <template v-if="accountData.posts.length">
                                <!--begin::Card-->
                                <div class="card card-custom gutter-b">
                                    <!--begin::Header-->
                                    <div class="card-header py-3">
                                        <div class="card-title align-items-start flex-column">
                                            <h3 class="card-label font-weight-bolder text-dark">
                                                Post Reach &amp; Impressions
                                            </h3>
                                            <span
                                                class="
                                                    text-muted
                                                    font-weight-bold font-size-sm
                                                    mt-1
                                                "
                                                >All posts submitted to Instagram</span
                                            >
                                        </div>
                                    </div>
                                    <!--end::Header-->
                                    <!-- Body -->
                                    <div class="card-body">
                                        <!-- <pre>
                                            {{ accountData.posts }}
                                        </pre> -->
                                        <p>Coming soon.</p>
                                    </div>
                                    <!-- End Body -->
                                </div>
                            </template>
                            <!--end:: Card -->
                            <template v-else>
                                <div class="card card-custom gutter-b">
                                    <div class="card-header">
                                        <div class="card-title">
                                            <span class="card-icon">
                                                <i class="flaticon2-chat-1 text-primary"></i>
                                            </span>
                                            <h3 class="card-label">
                                                No insights available for the moment
                                            </h3>
                                        </div>
                                    </div>
                                    <div class="card-body">
                                        <p class="lead">
                                            There are currently no insights available for this
                                            category. Please try again later.
                                        </p>
                                    </div>
                                </div>
                            </template>
                        </div>
                        <!--end:: Card -->
                    </transition-group>
                    <!-- End:: Groups -->
                </div>
                <!--end::row-->

                <!-- FIXME - Debug section -->
                <!-- <div class="col-xl-12">
                    <div class="card card-custom card-stretch gutter-b">
                        <div class="card-header">
                            <div class="card-title">
                                <h3 class="card-label">Raw output</h3>
                            </div>
                        </div>
                        <div class="card-body">
                            <pre>
                                    {{ accountData.insights }}
                                </pre
                            >
                        </div>
                    </div>
                </div> -->
                <!-- FIXME - End debug section -->
            </template>
        </template>
        <!-- End col 12 insights -->
    </div>
</template>

<style lang="scss" scoped>
@import "~bootstrap-vue/dist/bootstrap-vue.css";
</style>
<style scoped>
.campaign-link {
    cursor: pointer;
}

.list-enter-active,
.list-leave-active,
.list-move {
    transition: 500ms cubic-bezier(0.59, 0.12, 0.34, 0.95);
    transition-property: opacity, transform;
}

.list-enter {
    opacity: 0;
    transform: translateX(50px) scaleY(0.5);
}

.list-enter-to {
    opacity: 1;
    transform: translateX(0) scaleY(1);
}

.list-leave-active {
    position: absolute;
}

.list-leave-to {
    opacity: 0;
    transform: scaleY(0);
    transform-origin: center top;
}
</style>

<script>
// Defaults
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import axios from "axios";
// Components
import PagePreloader from "@/view/content/Custom/PagePreloader.vue";
import CustomAlerts from "@/view/content/Custom/CustomAlerts.vue";
import InfoBox from "@/view/content/Custom/InfoBox.vue";
import AccountBadgeWide from "@/view/content/Custom/AccountBadgeWide.vue";
import EnterpriseLogoDisplay from "@/view/content/Custom/EnterpriseLogoDisplay.vue";
// Buttons
import buttonAddToExistingHashtagSet from "@/view/content/Custom/buttonAddToExistingHashtagSet.vue";
import buttonCreateNewHashtagSet from "@/view/content/Custom/buttonCreateNewHashtagSet.vue";
import buttonCopyToClipboard from "@/view/content/Custom/buttonCopyToClipboard.vue";
import buttonScanInBulk from "@/view/content/Custom/buttonScanInBulk.vue";
// CLipboard - FIXME -
import ClipboardJS from "clipboard";
window.ClipboardJS = ClipboardJS;
// Mixins
import stringManagement from "@/mixins/stringManagement";
import axiosErrorManagement from "@/mixins/axiosErrorManagement";

export default {
    name: "AccountInsightsPage",
    mixins: [stringManagement, axiosErrorManagement],
    components: {
        PagePreloader,
        InfoBox,
        AccountBadgeWide,
        CustomAlerts,
        buttonAddToExistingHashtagSet,
        buttonCreateNewHashtagSet,
        buttonCopyToClipboard,
        buttonScanInBulk,
        EnterpriseLogoDisplay,
    },
    props: ["propFlashMessages"],
    data() {
        return {
            // Preloaders
            pageIsLoaded: false,
            modalisloading: false,

            // Error management
            errors: [],
            modalerrors: [],

            // Displayers
            displayAccounts: false,
            displayAccountData: false,
            displayHistorical: false,

            // Loaders
            modalIsLoading: false,
            loadingSearchResults: false,

            // My Accounts
            myAccounts: [],
            accountData: [],
            currentAccount: "",

            // Panel management
            currentPanel: "growth",

            // Charts
            audienceCountryOptions: {},
            audienceCountrySeries: [],

            audienceCityOptions: {},
            audienceCitySeries: [],

            audienceGenderOptions: {},
            audienceGenderSeries: [],

            audienceLocaleOptions: {},
            audienceLocaleSeries: [],

            reachImpressionsOptions: {},
            reachImpressionsSeries: [],

            reachImpressionsByMonthOptions: {},
            reachImpressionsByMonthSeries: [],

            growthFollowerOptions: {},
            growthFollowerSeries: [],

            // Forms
            formAddAccount: "",
        };
    },

    methods: {
        /**
         * NOTE - Get all account data
         */
        getMyAccounts() {
            axios
                .get("accountinsights/index")
                .then((response) => {
                    this.myAccounts = response.data.data;
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    this.pageIsLoaded = true;
                    this.displayAccounts = true;
                });
        },

        /**
         * NOTE - Get data when clicking on an account
         */
        clickSearch(account) {
            // Reset errors
            this.errors = [];

            // Set to current
            this.currentAccount = account.username;

            // TODO - Set loaders ?
            this.loadingSearchResults = true;

            // TODO - Reset all charts

            // Get insights for specific account
            axios
                .post("accountinsights/get", {
                    userid: account.userid,
                })
                .then((response) => {
                    // Prettify
                    var data = response.data;

                    // Handle
                    if (data.status == "OK") {
                        // Display
                        this.displayAccountData = true;

                        // Set data
                        this.accountData = data.data;

                        // Set panel
                        this.currentPanel = "growth";

                        // Start handling charts

                        if (typeof this.accountData.insights != "undefined") {
                            // NOTE - Audience Country Chart
                            var tempData = [];
                            var tempLabels = [];
                            if (this.accountData.insights.audience_country.length > 0) {
                                this.accountData.insights.audience_country.forEach(
                                    (currentItem) => {
                                        tempData.push(currentItem.value);
                                        tempLabels.push(currentItem.insight_sub);
                                    }
                                );
                                this.audienceCountrySeries = [
                                    {
                                        name: "Followers",
                                        data: tempData,
                                    },
                                ];
                                this.audienceCountryOptions = {
                                    chart: {
                                        type: "bar",
                                        height: 350,
                                    },
                                    title: {
                                        text: "Audience split by country",
                                    },
                                    plotOptions: {
                                        bar: {
                                            borderRadius: 4,
                                            horizontal: true,
                                        },
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    labels: tempLabels,
                                    // tooltip: {
                                    //     enabled: true,
                                    //     custom: function ({ series, seriesIndex, dataPointIndex, w }) {
                                    //         let total = 0;
                                    //         for (let x of series) {
                                    //             total += x;
                                    //         }
                                    //         let selected = series[seriesIndex];
                                    //         return (
                                    //             w.config.labels[seriesIndex] +
                                    //             ": " +
                                    //             selected +
                                    //             "(" +
                                    //             ((selected / total) * 100).toFixed(2) +
                                    //             "%)"
                                    //         );
                                    //     },
                                    // },
                                };
                            }

                            // NOTE - Audience City Chart
                            if (this.accountData.insights.audience_city.length > 0) {
                                var tempData = [];
                                var tempLabels = [];
                                this.accountData.insights.audience_city.forEach((currentItem) => {
                                    tempData.push(currentItem.value);
                                    tempLabels.push(currentItem.insight_sub);
                                });
                                this.audienceCitySeries = [
                                    {
                                        name: "City",
                                        data: tempData,
                                    },
                                ];
                                this.audienceCityOptions = {
                                    chart: {
                                        type: "bar",
                                        height: 350,
                                    },
                                    plotOptions: {
                                        bar: {
                                            borderRadius: 4,
                                            horizontal: true,
                                        },
                                    },
                                    title: {
                                        text: "Audience split by Main Cities",
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    labels: tempLabels,
                                };
                            }

                            // NOTE - Audience Gender CHart
                            if (this.accountData.insights.audience_gender_age.length > 0) {
                                var tempDataU = [];
                                var tempDataM = [];
                                var tempDataF = [];
                                var tempLabels = [];
                                this.accountData.insights.audience_gender_age.forEach(
                                    (currentItem) => {
                                        tempDataU.push(currentItem.U);
                                        tempDataF.push(currentItem.F);
                                        tempDataM.push(currentItem.M);
                                        tempLabels.push(currentItem.tranche);
                                    }
                                );
                                this.audienceGenderSeries = [
                                    {
                                        name: "Unspecified",
                                        data: tempDataU,
                                    },
                                    {
                                        name: "Men",
                                        data: tempDataM,
                                    },
                                    {
                                        name: "Women",
                                        data: tempDataF,
                                    },
                                ];
                                this.audienceGenderOptions = {
                                    chart: {
                                        type: "bar",
                                        height: 350,
                                        stacked: true,
                                        stackType: "100%",
                                    },
                                    title: {
                                        text: "Audience split by gender",
                                    },
                                    plotOptions: {
                                        bar: {
                                            borderRadius: 4,
                                            horizontal: true,
                                            stacked: true,
                                        },
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    labels: tempLabels,
                                    // tooltip: {
                                    //     y: {
                                    //         formatter: function (value, opts) {
                                    //             let percent =
                                    //                 opts.w.globals.seriesPercent[opts.seriesIndex][
                                    //                     opts.dataPointIndex
                                    //                 ];
                                    //             return percent.toFixed(0) + "%";
                                    //         },
                                    //     },
                                    // },
                                };
                            }

                            // NOTE - Audience Language
                            if (this.accountData.insights.audience_locale.length > 0) {
                                var tempData = [];
                                var tempLabels = [];
                                if (this.accountData.insights.audience_locale.length > 0) {
                                    this.accountData.insights.audience_locale.forEach(
                                        (currentItem) => {
                                            tempData.push(currentItem.value);
                                            tempLabels.push(currentItem.language);
                                        }
                                    );
                                }
                                this.audienceLocaleSeries = tempData;
                                this.audienceLocaleOptions = {
                                    chart: {
                                        type: "donut",
                                    },
                                    labels: tempLabels,
                                    plotOptions: {
                                        pie: {
                                            donut: {
                                                labels: {
                                                    show: true,
                                                },
                                            },
                                        },
                                    },
                                    legend: {
                                        verticalAlign: "middle",
                                    },
                                };
                            }

                            // NOTE - Impressions
                            if (this.accountData.insights.impressions.length > 0) {
                                var tempDataReach = [];
                                var tempDataImpressions = [];
                                var tempLabels = [];
                                this.accountData.insights.impressions.forEach((currentItem) => {
                                    tempDataImpressions.push(currentItem.value);
                                    tempLabels.push(currentItem.date);
                                });
                                this.accountData.insights.reach.forEach((currentItem) => {
                                    tempDataReach.push(currentItem.value);
                                    tempLabels.push(currentItem.date);
                                });
                                this.reachImpressionsSeries = [
                                    {
                                        name: "Impressions",
                                        data: tempDataImpressions,
                                    },
                                    {
                                        name: "Reach",
                                        data: tempDataReach,
                                    },
                                ];
                                this.reachImpressionsOptions = {
                                    chart: {
                                        type: "area",
                                        height: 350,
                                    },
                                    title: {
                                        text: "Reach and Impressions for last 30 days",
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    stroke: {
                                        curve: "smooth",
                                    },
                                    xaxis: {
                                        type: "date",
                                        categories: tempLabels,
                                    },
                                };
                            }

                            // NOTE - Impressions
                            if (this.accountData.insights.monthly.length > 0) {
                                var tempDataReach = [];
                                var tempDataImpressions = [];
                                var tempLabels = [];
                                this.accountData.insights.monthly.forEach((currentItem) => {
                                    tempDataImpressions.push(currentItem.impressions);
                                    tempDataReach.push(currentItem.reach);
                                    tempLabels.push(currentItem.date);
                                });
                                this.reachImpressionsByMonthSeries = [
                                    {
                                        name: "Impressions",
                                        data: tempDataImpressions,
                                    },
                                    {
                                        name: "Reach",
                                        data: tempDataReach,
                                    },
                                ];
                                this.reachImpressionsByMonthOptions = {
                                    chart: {
                                        type: "area",
                                        height: 350,
                                    },
                                    title: {
                                        text: "Reach and Impressions by Month",
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    stroke: {
                                        curve: "smooth",
                                    },
                                    xaxis: {
                                        type: "datetime",
                                        labels: {
                                            format: "MMM y",
                                            // datetimeFormatter: {
                                            //     year: "yyyy",
                                            //     month: "MMM 'yy",
                                            //     day: "dd MMM",
                                            //     hour: "HH:mm",
                                            // },
                                        },
                                        categories: tempLabels,
                                    },
                                    yaxis: {
                                        labels: {
                                            formatter: (value) => {
                                                return this.abbreviateNumber(value);
                                            },
                                        },
                                    },
                                    tooltip: {
                                        shared: true,
                                        x: {
                                            show: true,
                                            format: "MMMM y",
                                            formatter: undefined,
                                        },
                                    },
                                };
                            }

                            // NOTE - Growth
                            if (this.accountData.insights.growth.length > 0) {
                                var tempDataFollowers = [];
                                var tempDataFollowing = [];
                                var tempLabels = [];
                                this.accountData.insights.growth.forEach((currentItem) => {
                                    tempDataFollowers.push(currentItem.followers);
                                    tempDataFollowing.push(currentItem.followings);
                                    tempLabels.push(currentItem.date);
                                });
                                this.growthFollowerSeries = [
                                    {
                                        name: "Followers",
                                        data: tempDataFollowers,
                                    },
                                    // {
                                    //     name: "Reach",
                                    //     data: tempDataReach,
                                    // },
                                ];
                                this.growthFollowerOptions = {
                                    chart: {
                                        type: "area",
                                        height: 350,
                                    },
                                    title: {
                                        text: "Follower Growth",
                                    },
                                    dataLabels: {
                                        enabled: false,
                                    },
                                    stroke: {
                                        curve: "smooth",
                                    },
                                    xaxis: {
                                        type: "date",
                                        categories: tempLabels,
                                    },
                                };
                            }
                        }
                    }

                    // Do error handling
                    if (data.messages.length > 0) {
                        data.messages.forEach((message) => {
                            this.errors.push({
                                type: message.type,
                                message: message.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.errors.push(this.handleAxiosErrors(error));
                    this.displayAccountData = false;
                })
                .finally(() => {
                    // Close off preloader
                    this.loadingSearchResults = false;
                });
        },

        /**
         * NOTE - Add new account
         */
        openAddAccountModal() {
            // Reset errors
            this.modalerrors = [];
            this.modalIsLoading = false;

            // Open modal
            this.$refs["add-account-modal"].show();
        },
        addAccount() {
            // Prevent double submission
            if (this.modalIsLoading == true) {
                return;
            }

            // Set loading
            this.modalIsLoading = true;

            // Perform the thing
            axios
                .get("accountinsights/add")
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        window.location.href = data.data;
                        this.$refs["add-account-modal"].hide();
                    } else {
                        // TODO
                    }
                })
                .catch((error) => {
                    this.modalerrors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close preloader
                    this.modalIsLoading = false;
                });
        },

        /**
         * NOTE - Delete existing account
         */
        openDeleteModal() {
            // Reset
            this.modalerrors = [];

            // Open modal
            this.$refs["modal-delete"].show();
        },
        deleteAccount() {
            // First remove from the JS array so we know that shit works yo
            var foundkey = this.myAccounts.findIndex(
                (x) => x.id === this.accountData.account_data.id
            );

            // Reset errors
            this.modalerrors = [];

            // Set loader
            this.modalisloading = true;

            // Perform query
            axios
                .post("accountinsights/delete", {
                    userid: this.accountData.account_data.userid,
                })
                .then((response) => {
                    // Make pretty
                    var data = response.data;

                    // Check status
                    if (data.status == "OK") {
                        // Remove from array
                        this.myAccounts.splice(foundkey, 1);

                        // Add a success message
                        this.errors.push({
                            type: "success",
                            message: "Account successfully removed.",
                        });

                        // Hide modal
                        this.$refs["modal-delete"].hide();

                        // Display default
                        this.displayAccountData = false;
                    }

                    // Add errors
                    if (data.messages.length > 0) {
                        data.messages.forEach((message) => {
                            this.modalerrors.push({
                                type: message.type,
                                message: message.message,
                            });
                        });
                    }
                })
                .catch((error) => {
                    this.modalerrors.push(this.handleAxiosErrors(error));
                })
                .finally(() => {
                    // Close off preloader
                    this.modalisloading = false;
                });
        },

        /**
         * NOTE - Handle GET parameters
         */

        handleGET() {
            // Get all parameters
            var params = {};
            location.search
                .substr(1)
                .split("&")
                .forEach(function (item) {
                    params[item.split("=")[0]] = item.split("=")[1];
                });

            // Reset errors
            this.errors = [];

            // Handle errors
            if (typeof params.error != "undefined") {
                var message = params.error;

                if (message == "unknown_error") {
                    this.errors.push({
                        type: "danger",
                        message:
                            "There was an unknown error processing your request. Please try again. If the issue persists, please contact an Admin ASAP so we can look into it for you.",
                    });
                }

                if (message == "missing_scope") {
                    this.errors.push({
                        type: "danger",
                        message:
                            "In order to obtain insights from your Instagram accounts you need to check all the boxes when connecting Hashtastic to your Facebook account. Please try adding the account again. If the issue persists, please contact an Admin.",
                    });
                }
                if (message == "missing_pages") {
                    this.errors.push({
                        type: "danger",
                        message:
                            "No Facebook Pages were selected while linking Hashtastic to Facebook. Please try adding the accounts again. If the issue persists, please contact an Admin.",
                    });
                }
                if (message == "no_ig_account") {
                    this.errors.push({
                        type: "danger",
                        message:
                            "It looks like your Instagram account isn't connected to a Facebook page. Please make sure your Instagram account is connected to a Facebook account via the Instagram app, and try again.",
                    });
                }
                if (message == "no_accounts") {
                    this.errors.push({
                        type: "warning",
                        message: "No accounts were selected to be added to the tracker.",
                    });
                }
                if (message == "not_logged_in") {
                    this.errors.push({
                        type: "warning",
                        message:
                            "It looks like you took too long to go through the Facebook login, please try again. If the issue persists, please contact an Admin.",
                    });
                }
                if (message == "authcode_already_used") {
                    this.errors.push({
                        type: "danger",
                        message:
                            "The authorisation code provided by Facebook has already been used. Please try adding your accounts again. If the issue persists, please contact an Admin.",
                    });
                }
                if (message == "accesstoken_error") {
                    this.errors.push({
                        type: "danger",
                        message:
                            "There was an error retrieving an access token from Facebook. Please try adding your accounts again. If the issue persists, please contact an Admin.",
                    });
                }
                if (message == "accesstoken_denied") {
                    this.errors.push({
                        type: "danger",
                        message:
                            "There was an error retrieving an access token from Facebook. Please try adding your accounts again. If the issue persists, please contact an Admin.",
                    });
                }

                // Add the error message to the Window
                window.hashtastic_message = message;
                if (window.opener) {
                    window.close();
                }
            } else if (typeof params.success != "undefined") {
                this.errors.push({
                    type: "success",
                    message: "Successfully added accounts.",
                });
                // This should close the tab but only works if that page was opened by a JS script, which is the case for another script that brought us here
                if (window.opener) {
                    window.close();
                }
            }
        },

        // setPanel
        setPanel(panel) {
            // Reset errors if we're switching panels
            if (this.currentPanel != panel) {
                this.errors = [];
            }
            // Reset loader
            this.formIsLoading = false;
            // Set panel
            this.currentPanel = panel;
        },
    },

    computed: {},

    mounted() {
        // Parse props
        // this.parsePropFlashMessages();

        // Handle GET messages
        this.handleGET();

        // Get accounts
        this.getMyAccounts();

        // Breadcrumb
        this.$store.dispatch(SET_BREADCRUMB, [
            { title: "Instagram Insights", route: "accountinsights" },
        ]);
    },
};
</script>
